import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import {
  PATH_MATERIAL_GENERAL,
  PATH_MATERIALS,
} from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { MaterialListCmp_AllMaterials_QueryDocument } from '../../../components/MaterialList/hooks/useFindAllMaterialsQuery.hook';
import { MaterialPage_OneMaterial_QueryDocument } from '../../../pages/Material/hooks/useFindOneMaterialQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';

import { materialForm2ApiMapper } from '../mappers/material.form2api.mapper';
import type { MaterialForm_Material } from '../fragments/material.fragment';
import type { MaterialFormTab } from '../enums/tab.enum';

import { useCreateMaterialMutation } from './useCreateMaterialMutation.hook';
import { useUpdateMaterialMutation } from './useUpdateMaterialMutation.hook';
import type { MaterialForm_Material_Create_Result } from './useCreateMaterialMutation.hook';
import type { MaterialForm_Material_Foau_Result } from './useUpdateMaterialMutation.hook';

export type MaterialMutationResult =
  MaterialForm_Material_Create_Result | MaterialForm_Material_Foau_Result;

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: MaterialMutationResult) => void;
  readonly materialId?: Id;
}

export const useMaterialSubmit = ({
  formVariant,
  handleSuccess,
  materialId,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createMutation } = useCreateMaterialMutation();
  const { mutate: updateMutation } = useUpdateMaterialMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<MaterialForm_Material, MaterialFormTab>
    >) => {
      setWizardError(undefined);

      const onSuccess = (result: MaterialMutationResult) => {
        if (formVariant === FormVariant.Creation) {
          const { _id } = (result as MaterialForm_Material_Create_Result)
            .createMaterial;

          enqueueSnackbar('Votre nouveau matériel a bien été créé.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_MATERIAL_GENERAL, { id: _id }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MaterialListCmp_AllMaterials_QueryDocument),
            ],
          });
        } else {
          enqueueSnackbar('Votre matériel a bien été mis à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MaterialPage_OneMaterial_QueryDocument),
              { id: materialId },
            ],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(PATH_MATERIALS);
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({ messages: userFriendlyErrors(error as BackError) });

        setSubmitting(false);
      };

      if (formVariant === FormVariant.Creation) {
        createMutation(
          {
            material: materialForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else {
        updateMutation(
          {
            filter: { _id: materialId ?? EMPTY_ID },
            update: materialForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      materialId,
      buildViewSnackbarAction,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      navigate,
      queryClient,
      createMutation,
      updateMutation,
    ],
  );

  return { handleSubmit };
};
